<template>
  <div class="atb-divide-y atb-divide-gray-300 atb-py-6">
    <div class="atb-pb-4">
      <label
        id="systemDetailsSystemNameTitleLabel"
        class="atb-ml-20"
      >
        <span class="card-title">
          {{ DomPurify.sanitize(systemData.name) }}
        </span>
      </label>
    </div>
    <Card class="card-size atb-ml-20 atb-pt-6">
      <template #body>
        <Form>
          <div
            class="atb-grid atb-grid-cols-1 atb-gap-y-4 atb-gap-x-14 atb-px-2 atb-py-4 atb-w-1/3"
          >
            <div
              :class="{
                'atb-flex atb-items-center': systemNameErrorMsg.length,
              }"
            >
              <FormField
                :label="$t('systemName')"
                required="true"
                :message="systemNameErrorMsg"
                :message-type="'error'"
              >
                <Textbox
                  id="systemName"
                  v-model.trim="systemData.name"
                  maxlength="30"
                  :state="systemNameErrorMsg.length ? 'error' : 'basic'"
                >
                </Textbox>
              </FormField>
            </div>
            <div>
              <FormField :label="$t('notes')">
                <Textarea
                  id="notes"
                  v-model="systemData.notes"
                  field-control-class="atb-h-24"
                />
              </FormField>
            </div>
            <FormField
              :label="$t('createdOn')"
              form-field-class="atb-flex atb-gap-x-1"
              label-class="atb-w-1/3"
            >
              <div>
                {{
                  isNew
                    ? dayjs().format('MMM DD YYYY HH:mm:ss')
                    : dayjs(systemData.createdAt).format('MMM DD YYYY HH:mm:ss')
                }}
                by {{ systemData.createdBy }}
              </div>
            </FormField>
            <FormField
              :label="$t('updatedOn')"
              form-field-class="atb-flex atb-gap-x-1"
              label-class="atb-w-1/3"
            >
              <div>
                {{
                  systemData.updatedAt
                    ? dayjs(systemData.updatedAt).format('MMM DD YYYY HH:mm:ss')
                    : 'Not Specified'
                }}
              </div>
            </FormField>
          </div>
          <div class="atb-flex atb-flex-row"></div>
        </Form>
        <div class="atb-flex atb-flex-row justify-between">
          <div class="eds-table-wrap atb-w-9/20">
            <div class="atb-px-2 atb-pt-2 atb-pb-4">
              <div class="eds-heading heading-sm atb-pb-2">
                {{ $t('allAttributes') }}
              </div>
              <Textbox
                id="allAttributesSearch"
                v-model="allAttributesSearch"
                container-label-class="atb-w-full"
                autocomplete="off"
                :placeholder="$t('search')"
              >
              </Textbox>
            </div>
            <AgGridVue
              class="ag-theme-eds atb-w-full atb-h-full"
              :style="{ height: '250px' }"
              :column-defs="columnDefs"
              :row-selection="'multiple'"
              :row-multi-select-with-click="true"
              :suppress-row-click-selection="true"
              :pagination="false"
              @grid-ready="allAttributesOnGridReady"
            />
            <div class="atb-bg-gray-100">
              <Button
                motif="tertiary"
                title="Reset Search"
                class-name="focus:atb-outline-none"
                @click="allAttributesSearch = ''"
              >
                <template #icon>
                  <span
                    class="material-icons-outlined atb-h-auto atb-cursor-pointer"
                  >
                    cancel
                  </span>
                </template>
              </Button>
            </div>
          </div>
          <div
            class="atb-flex atb-flex-col atb-justify-center atb-items-center"
          >
            <Button
              id="systemDetailsRemoveAttributesFromSystemButton"
              motif="tertiary"
              @click="removeAttributesFromSystem"
            >
              <template #icon>
                <span class="material-icons atb-h-auto cursor-pointer">
                  arrow_back
                </span>
              </template>
            </Button>
            <Button
              id="systemDetailsAddAttributesToSystemButton"
              motif="tertiary"
              @click="addAttributesToSystem"
            >
              <template #icon>
                <span class="material-icons atb-h-auto cursor-pointer">
                  arrow_forward
                </span>
              </template>
            </Button>
          </div>
          <div class="eds-table-wrap atb-w-9/20">
            <div class="atb-px-2 atb-pt-2 atb-pb-4">
              <div class="eds-heading heading-sm atb-pb-2">
                {{ $t('systemAttributes') }}
              </div>
              <Textbox
                id="systemAttributesSearch"
                v-model="systemAttributesSearch"
                container-label-class="atb-w-full"
                autocomplete="off"
                :placeholder="$t('search')"
              />
            </div>
            <AgGridVue
              class="ag-theme-eds atb-w-full atb-h-full"
              :style="{ height: '250px' }"
              :column-defs="columnDefs"
              :row-selection="'multiple'"
              :row-multi-select-with-click="true"
              :suppress-row-click-selection="true"
              :suppress-cell-selection="true"
              :pagination="false"
              @grid-ready="systemAttributesOnGridReady"
            />
            <div class="bg-gray-100">
              <Button
                motif="tertiary"
                title="Reset Search"
                class-name="focus:atb-outline-none"
                @click="systemAttributesSearch = ''"
              >
                <template #icon>
                  <span
                    class="material-icons-outlined atb-h-auto cursor-pointer"
                  >
                    cancel
                  </span>
                </template>
              </Button>
            </div>
          </div>
        </div>
        <div class="atb-flex atb-flex-row atb-justify-end atb-gap-x-8 atb-pt-8">
          <Button
            id="delete"
            type="submit"
            motif="basic"
            :label="$t('delete')"
            :title="$t('delete')"
            :disabled="isNew"
            @click="displayDeleteConfirmation = true"
          />
          <Button
            id="save"
            type="submit"
            motif="basic"
            :label="$t('save')"
            :title="$t('save')"
            :disabled="isSaveDisabled"
            @click="saveSystemHandler"
          />
          <Button
            id="deploy"
            type="submit"
            motif="basic"
            :label="$t('deploy')"
            :title="$t('deploy')"
            :disabled="isNew || readOnly"
            @click="deployModalRef?.changeModalVisibility()"
          />
        </div>
      </template>
    </Card>
  </div>
  <SystemsDeploy
    ref="deployModalRef"
    :system-id="systemId"
    :system-name="systemDetails.name"
  />
  <Modal
    :show="showSaveConfirmation"
    container-class="atb-w-96"
    body-class="atb-border-none"
    footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
    @onModalClose="showSaveConfirmation = false"
  >
    <template #header> {{ $t('areYouSure') }} </template>
    <template #body>
      <div class="atb-flex atb-flex-col atb-gap-4">
        {{ $t('checkingSystemSave') }}
      </div>
    </template>
    <template #footer>
      <Button
        id="saveSystemConfirmButton"
        @click="saveSystem"
      >
        {{ $t('ok') }}</Button
      >

      <Button
        motif="basic"
        @click="showSaveConfirmation = false"
      >
        {{ $t('cancel') }}
      </Button>
    </template>
  </Modal>
  <Modal
    :show="displayDeleteConfirmation"
    container-class="atb-w-96"
    body-class="atb-border-none"
    footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
    @onModalClose="displayDeleteConfirmation = false"
  >
    <template #header> {{ $t('areYouSure') }} </template>
    <template #body>
      <div
        id="deleteSuccessSystemLabel"
        class="atb-flex atb-flex-col atb-gap-4"
      >
        {{ $t('checkingSystemDeletion') }}
      </div>
    </template>
    <template #footer>
      <Button
        motif="basic"
        @click="displayDeleteConfirmation = false"
      >
        {{ $t('cancel') }}
      </Button>
      <Button
        id="deleteSystemConfirmButton"
        @click="deleteSystem"
      >
        {{ $t('ok') }}</Button
      >
    </template>
  </Modal>
</template>
<script lang="ts" setup>
  import { useAttributesStore } from '@/app/features/ams/attributes/attributes-store';
  import SystemsDeploy from '@/app/features/ams/systems/components/SystemsDeploy.vue';
  import systemsApi from '@/app/features/ams/systems/systems-service';
  import { useSystemsStore } from '@/app/features/ams/systems/systems-store';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { useAuthStore } from '@/app/shared/stores/auth-store';
  import { SystemInfo } from '@/app/shared/types/systeminfo.interface';
  import { SYSTEM_ALPHANUMERIC_REGEX } from '@/app/utils/regexpr-const';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Card from '@atb-ui/eds-vue/src/components/Card.vue';
  import Form from '@atb-ui/eds-vue/src/components/Form/Form.vue';
  import FormField from '@atb-ui/eds-vue/src/components/Form/FormField.vue';
  import Modal from '@atb-ui/eds-vue/src/components/Modal.vue';
  import Textarea from '@atb-ui/eds-vue/src/components/Textarea.vue';
  import Textbox from '@atb-ui/eds-vue/src/components/Textbox.vue';
  import { AgGridEvent, ColumnApi, GridApi } from 'ag-grid-community';
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';
  import dayjs from 'dayjs';
  import { computed, Ref, ref, toRefs, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import DomPurify from 'dompurify';
  import { ModalActions } from '@/app/shared/types/modal-actions.interface';
  import AccessGuard from '@/app/shared/guards/access-guard';

  interface DetailsEvent {
    (_event: 'systemDeleted'): void;
  }
  interface DetailsProps {
    clientId: number;
    systemId: number;
  }
  const emit = defineEmits<DetailsEvent>();
  const props = defineProps<DetailsProps>();
  const { t } = useI18n();
  const attributesStore = useAttributesStore();
  const systemsStore = useSystemsStore();
  const alertStore = useAlertStore();
  const { clientId, systemId } = toRefs(props);
  const systemDetails = systemsStore.systemDetails.get(systemId.value);
  const systemData = ref({ ...systemDetails.systemData });
  const deployModalRef: Ref<ModalActions | null> = ref(null);
  const authStore = useAuthStore();
  const dataHasChanged = ref(true);
  const dataHasNotChanged = ref(false);

  const systemNameErrorMsg = ref('');
  const showSaveConfirmation = ref(false);
  const displayDeleteConfirmation = ref(false);

  const allAttributesSearch = ref('');
  const systemAttributesSearch = ref('');

  const allAttributesGrid = ref(null) as Ref<GridApi | null>;
  const allAttributesGridColumnApi = ref(null) as Ref<ColumnApi | null>;
  const systemAttributesGrid = ref(null) as Ref<GridApi | null>;
  const systemAttributesGridColumnApi = ref(null) as Ref<ColumnApi | null>;

  const allAttributesSystem = ref([]) as Ref<Array<any>>;
  const systemAttributes = ref([]) as Ref<Array<any>>;

  const dirtyStatus = computed(() => {
    return systemsStore.systemDetails.get(systemId.value)?.dirty;
  });

  const isSaveDisabled = computed((): boolean => {
    return !dirtyStatus.value;
  });

  const readOnly = computed(() => !AccessGuard.canModify('DEPLOY'));

  const isCopy = computed(() => {
    return systemsStore.systemDetails.get(systemId.value)?.isCopied?.isCopy;
  });

  const copiedId = computed(() => {
    return systemsStore.systemDetails.get(systemId.value)?.isCopied.id;
  });

  const isNew = computed(() => {
    return systemsStore.systemDetails.get(systemId.value)?.isNew;
  });

  watch(
    systemData,
    () => {
      setDirtyField(dataHasChanged.value);
    },
    { deep: true }
  );

  watch(allAttributesSearch, () => {
    allAttributesGrid.value?.setQuickFilter(allAttributesSearch.value);
  });

  watch(systemAttributesSearch, () => {
    systemAttributesGrid.value?.setQuickFilter(systemAttributesSearch.value);
  });

  const columnDefs = [
    {
      field: 'id',
      headerName: '',
      width: 56,
      suppressSizeToFit: true,
      headerCheckboxSelection: true,
      checkboxSelection: true,
    },
    {
      field: 'shortName',
      headerName: t('attribute'),
      sortable: true,
      suppressSizeToFit: false,
    },
  ];

  const allAttributesOnGridReady = async ({ api, columnApi }: AgGridEvent) => {
    allAttributesGrid.value = api;
    allAttributesGridColumnApi.value = columnApi;
    allAttributesGrid.value.showLoadingOverlay();
    await attributesStore.fetchSystemAllAttributes(clientId.value, {
      page: 0,
    });
    await systemsStore.fetchSystemAttributes(
      clientId.value,
      isCopy.value ? copiedId.value : systemId.value
    );
    allAttributesSystem.value = attributesStore.allAttributesSystem;
    systemAttributes.value.forEach((systemAttribute) => {
      allAttributesSystem.value = allAttributesSystem.value.filter(
        (attribute) => attribute.id !== systemAttribute.id
      );
    });
    allAttributesGrid.value.setRowData(allAttributesSystem.value);
    allAttributesGrid.value.sizeColumnsToFit();
    allAttributesSystem.value.length > 0
      ? allAttributesGrid.value.hideOverlay()
      : allAttributesGrid.value.showNoRowsOverlay();
  };

  const systemAttributesOnGridReady = async ({
    api,
    columnApi,
  }: AgGridEvent) => {
    systemAttributesGrid.value = api;
    systemAttributesGridColumnApi.value = columnApi;
    systemAttributesGrid.value.showLoadingOverlay();
    await systemsStore.fetchSystemAttributes(
      clientId.value,
      isCopy.value ? copiedId.value : systemId.value
    );
    if (systemsStore.systemAttributes.length > 0) {
      systemAttributes.value = systemsStore.systemAttributes;
      systemAttributesGrid.value.setRowData(systemsStore.systemAttributes);
      systemAttributesGrid.value.sizeColumnsToFit();
      systemAttributesGrid.value.hideOverlay();
    } else {
      systemAttributesGrid.value.showNoRowsOverlay();
    }
  };

  const addAttributesToSystem = () => {
    systemAttributesGrid.value?.showLoadingOverlay();
    allAttributesGrid.value?.showLoadingOverlay();
    const selectedNodes = allAttributesGrid.value?.getSelectedNodes();
    const selectedAttributes = selectedNodes?.map((elem: any) => elem.data);
    allAttributesSystem.value = allAttributesSystem.value.filter(
      (attribute) => {
        return !selectedAttributes?.some(
          (selectedAttribute) => selectedAttribute.id === attribute.id
        );
      }
    );
    systemAttributes.value.push(...(selectedAttributes ?? []));
    allAttributesGrid.value?.deselectAll();
    allAttributesGrid.value?.setRowData(allAttributesSystem.value);
    systemAttributesGrid.value?.setRowData(systemAttributes.value);
    setDirtyField(dataHasChanged.value);
    systemAttributesGrid.value?.sizeColumnsToFit();
  };

  const removeAttributesFromSystem = () => {
    systemAttributesGrid.value?.showLoadingOverlay();
    allAttributesGrid.value?.showLoadingOverlay();
    const selectedNodes = systemAttributesGrid.value?.getSelectedNodes();
    const selectedAttributes = selectedNodes?.map((elem: any) => elem.data);
    systemAttributes.value = systemAttributes.value.filter((attribute) => {
      return !selectedAttributes?.some(
        (selectedAttribute) => selectedAttribute.id === attribute.id
      );
    });
    allAttributesSystem.value.push(...(selectedAttributes || []));
    systemAttributesGrid.value?.deselectAll();
    systemAttributesGrid.value?.setRowData(systemAttributes.value);
    allAttributesGrid.value?.setRowData(allAttributesSystem.value);
    setDirtyField(dataHasChanged.value);
    systemAttributesGrid.value?.sizeColumnsToFit();
  };

  const setDirtyField = (value: boolean) => {
    systemsStore.setTabDirtyFieldById(systemId.value, value);
  };

  const setNewField = (value: boolean) => {
    systemsStore.setTabNewFieldById(systemId.value, value);
  };

  const saveSystemHandler = (): void => {
    showSaveConfirmation.value = validateRequiredFields();
  };

  const validateRequiredFields = (): boolean => {
    const { name } = systemData.value;
    let isReqFieldError = false;
    const isNotValidName = !new RegExp(SYSTEM_ALPHANUMERIC_REGEX).test(name);

    if (!name.length) {
      systemNameErrorMsg.value = t('emptyField');
      isReqFieldError = true;
    } else if (isNotValidName) {
      systemNameErrorMsg.value = t('systemNameInvalid');
    } else {
      systemNameErrorMsg.value = '';
    }
    return !(isReqFieldError || isNotValidName);
  };

  const saveSystem = async () => {
    try {
      showSaveConfirmation.value = false;
      const { name, notes } = systemData.value;
      const systemInfo = {
        name,
        notes,
        createdBy: authStore.username.split('@').shift(),
        attributes: systemAttributes.value.map((elem) => elem.id),
      } as unknown as SystemInfo;
      if (systemData.value.id) {
        updateSystem(systemInfo);
      } else {
        if (isCopy.value) {
          const copyFromSystemId = systemsStore.systemDetails.get(
            systemId.value
          )?.isCopied.id;
          copySystem(copyFromSystemId, systemInfo);
        } else {
          createNewSystem(systemInfo);
        }
      }
    } catch (error: any) {
      const message =
        error?.errors?.[0].error || 'Unkown error returned from server';
      alertStore.showNotification(message, 'Systems', Motif.ERROR);
    }
  };

  const updateSystem = async (systemInfo: SystemInfo) => {
    try {
      await systemsApi.updateSystem(
        clientId.value,
        systemData.value.id,
        systemInfo
      );
      setDirtyField(dataHasNotChanged.value);
      setNewField(false);
      if (systemData.value.name !== systemInfo.name) {
        systemsStore.setRefreshGridData(true);
        systemsStore.updateTabNameFieldById(systemId.value, systemInfo.name);
        systemData.value.name = systemInfo.name;
      }
      alertStore.showNotification(
        t('systemSaved'),
        t('systemSaved'),
        Motif.SUCCESS
      );
    } catch (error: any) {
      const message =
        error?.errors?.[0].error || 'Unkown error returned from server';
      alertStore.showNotification(message, 'Systems', Motif.ERROR);
    }
  };

  const copySystem = async (
    copyFromSystemId: number,
    copiedSystemData: any
  ) => {
    const response = await systemsApi.copySystemById(
      clientId.value,
      copyFromSystemId,
      copiedSystemData
    );
    systemsStore.setTabIsCopyStatusToDefaultById(systemId.value);
    alertStore.showNotification(t('systemSaved'), t('systems'), Motif.SUCCESS);
    systemsStore.setRefreshGridData(true);
    systemsStore.saveCreatedSystem(systemId.value, response.data?.data);
  };

  const createNewSystem = async (systemInfo: SystemInfo) => {
    const response = await systemsApi.createSystem(clientId.value, systemInfo);
    alertStore.showNotification(t('systemSaved'), t('systems'), Motif.SUCCESS);
    systemsStore.setRefreshGridData(true);
    systemsStore.saveCreatedSystem(systemId.value, response.data?.data);
    setDirtyField(dataHasNotChanged.value);
    await systemsStore.fetchSystemAttributes(
      clientId.value,
      response.data?.data.id
    );
  };

  const deleteSystem = async () => {
    displayDeleteConfirmation.value = false;
    await systemsApi
      .deleteSystemById(clientId.value, Number(systemId.value))
      .then((_resp) => {
        emit('systemDeleted');
        systemsStore.deleteAttribute(Number(systemId.value));
      })
      .catch(() => {
        alertStore.showNotification(
          t('unknownError'),
          t('systems'),
          Motif.SUCCESS
        );
      });
  };
</script>
